import { FC } from 'react'
import { Button, Card, Col, Input, InputNumber, Row, Segmented, Select, Space, Switch, Tag, Tooltip, Typography } from 'antd'
import { DeleteOutlined } from '@ant-design/icons'
import { translate } from '../../../core'
import { useStatesForCountry } from '../hook/useStatesForCountry'
import {
  SHIPPING_RULE_EFFECT_COLOR,
  ShippingCountry,
  ShippingRuleDraft,
  ShippingRuleEffect,
} from '../type/shipping-method'

interface Props {
  rule: ShippingRuleDraft
  index: number
  countries: ShippingCountry[]
  basePrice: number
  onChange: (patch: Partial<ShippingRuleDraft>) => void
  onRemove: () => void
}

const ANY = undefined

function describe(rule: ShippingRuleDraft, countries: ShippingCountry[]): string {
  const parts: string[] = []

  const country = rule.countryCode
    ? countries.find(c => c.code === rule.countryCode)?.name ?? rule.countryCode
    : translate('any_country')
  parts.push(rule.state ? `${rule.state}, ${country}` : country)

  const { minCartValue: min, maxCartValue: max } = rule
  if (min != null && max != null) parts.push(`$${min} – $${max}`)
  else if (min != null) parts.push(translate('cart_at_least', `$${min}`))
  else if (max != null) parts.push(translate('cart_at_most', `$${max}`))
  else parts.push(translate('any_cart_value'))

  return parts.join(' · ')
}

export const ShippingRuleForm: FC<Props> = ({
  rule,
  index,
  countries,
  basePrice,
  onChange,
  onRemove,
}) => {
  const states = useStatesForCountry(rule.countryCode, countries)
  const effect = rule.effect ?? ShippingRuleEffect.Include
  const isExclusion = effect === ShippingRuleEffect.Exclude

  return (
    <Card
      size="small"
      style={{ marginBottom: 12, opacity: rule.isActive === false ? 0.6 : 1 }}
      title={
        <Space>
          <Typography.Text strong>{translate('rule')} {index + 1}</Typography.Text>
          <Tag color={SHIPPING_RULE_EFFECT_COLOR[effect]}>
            {translate(isExclusion ? 'rule_effect_exclude' : 'rule_effect_include')}
          </Tag>
          <Typography.Text type="secondary">{describe(rule, countries)}</Typography.Text>
        </Space>
      }
      extra={
        <Space>
          <Tooltip title={translate('status')}>
            <Switch
              size="small"
              checked={rule.isActive !== false}
              onChange={checked => onChange({ isActive: checked })}
            />
          </Tooltip>
          <Tooltip title={translate('delete')}>
            <Button type="text" shape="circle" danger icon={<DeleteOutlined />} onClick={onRemove} />
          </Tooltip>
        </Space>
      }
    >
      <Row gutter={[12, 12]}>
        <Col xs={24}>
          <Typography.Text type="secondary">{translate('rule_effect')}</Typography.Text>
          <div>
            <Segmented
              value={effect}
              onChange={value => {
                const next = value as ShippingRuleEffect
                onChange(next === ShippingRuleEffect.Exclude
                  ? { effect: next, priceOverride: null }
                  : { effect: next })
              }}
              options={[
                { value: ShippingRuleEffect.Include, label: translate('rule_effect_include') },
                { value: ShippingRuleEffect.Exclude, label: translate('rule_effect_exclude') },
              ]}
            />
          </div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {translate(isExclusion ? 'rule_effect_exclude_hint' : 'rule_effect_include_hint')}
          </Typography.Text>
        </Col>

        <Col xs={24} md={12}>
          <Typography.Text type="secondary">{translate('destination_country')}</Typography.Text>
          <Select
            value={rule.countryCode ?? ANY}
            onChange={code => onChange({ countryCode: code ?? null, state: null })}
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={translate('any_country')}
            style={{ width: '100%' }}
            options={countries.map(c => ({ value: c.code, label: `${c.name} (${c.code})` }))}
          />
        </Col>

        <Col xs={24} md={12}>
          <Typography.Text type="secondary">{translate('destination_state')}</Typography.Text>
          {states.length > 0 ? (
            <Select
              value={rule.state ?? ANY}
              onChange={state => onChange({ state: state ?? null })}
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={translate('any_state')}
              style={{ width: '100%' }}
              options={states.map(s => ({ value: s.key, label: `${s.name} (${s.key})` }))}
            />
          ) : (
            <Input
              value={rule.state ?? ''}
              onChange={e => onChange({ state: e.target.value || null })}
              placeholder={translate('any_state')}
            />
          )}
        </Col>

        <Col xs={24} md={8}>
          <Typography.Text type="secondary">{translate('min_cart_value')}</Typography.Text>
          <InputNumber
            value={rule.minCartValue ?? null}
            onChange={value => onChange({ minCartValue: value ?? null })}
            min={0}
            precision={2}
            prefix="$"
            placeholder={translate('no_minimum')}
            style={{ width: '100%' }}
          />
        </Col>

        <Col xs={24} md={8}>
          <Typography.Text type="secondary">{translate('max_cart_value')}</Typography.Text>
          <InputNumber
            value={rule.maxCartValue ?? null}
            onChange={value => onChange({ maxCartValue: value ?? null })}
            min={0}
            precision={2}
            prefix="$"
            placeholder={translate('no_maximum')}
            style={{ width: '100%' }}
          />
        </Col>

        <Col xs={24} md={8}>
          <Typography.Text type="secondary">{translate('price_override')}</Typography.Text>
          <InputNumber
            value={isExclusion ? null : rule.priceOverride ?? null}
            onChange={value => onChange({ priceOverride: value ?? null })}
            disabled={isExclusion}
            min={0}
            precision={2}
            prefix="$"
            placeholder={isExclusion
              ? translate('exclusion_has_no_price')
              : translate('uses_base_price', `$${basePrice.toFixed(2)}`)}
            style={{ width: '100%' }}
          />
        </Col>

        <Col xs={24}>
          <Typography.Text type="secondary">{translate('label')}</Typography.Text>
          <Input
            value={rule.label ?? ''}
            onChange={e => onChange({ label: e.target.value || null })}
            placeholder={translate('rule_label_placeholder')}
            maxLength={255}
          />
        </Col>
      </Row>
    </Card>
  )
}
